import { useEffect, useMemo, useRef, useState } from 'react';
import { find, isEmpty, isUndefined, range } from 'lodash';
import moment from 'moment';
import { Box, IBoxProps, View, useMediaQuery, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, LayoutRectangle, Platform } from 'react-native';
import { G, Line, LinearGradient, Rect, Stop, Svg, Text as SvgText } from 'react-native-svg';
import {
  VictoryArea,
  VictoryAxis,
  VictoryBrushContainer,
  VictoryChart,
  VictoryContainer,
  VictoryLine,
} from 'victory-native';

import { LinkedInstanceChartDataDto, ThresholdsDto } from '@pimm/services/lib/store-equipment';
import { Text } from '@pimm/base';
import { FIVE_MIN_MS, createMetricsets, getThresholdOffset } from '../_helper';
import { ThresholdColors } from '../consts';
import { useChartGesture } from '../hooks/chart-gesture.hook';
import { DoorEventIcon } from '../icons/door-event';
import { WifiIcon } from '../icons/wifi';
import { ChartTooltipFlyout } from './chart-tooltip-flyout';
import { EventLog } from './event-logs';

// Custom Victory tickLabel for threshold axis: a small white pill with a red
// outline + red text.
const ThresholdChip = (props: any) => {
  const { x, y, text } = props;
  if (text === '' || text == null) return null;
  const w = 38;
  const h = 18;
  return (
    <G>
      <Rect x={x} y={y - h / 2} width={w} height={h} rx={h / 2} ry={h / 2} fill="white" stroke={ThresholdColors.Red} strokeWidth={1} />
      <SvgText
        x={x + w / 2}
        y={y + 4}
        textAnchor="middle"
        fontFamily="'Inter', sans-serif"
        fontSize={11}
        fontWeight={600}
        fill={ThresholdColors.Red}
      >
        {text}
      </SvgText>
    </G>
  );
};

// Custom brush handle — rounded blue pill with grip lines. Hidden until the
// user actually drags a selection (`hasSelection` prop).
const BrushHandle = (props: any) => {
  const { x, y, width, height, hasSelection } = props;
  if (!hasSelection) return null;
  if (typeof x !== 'number' || typeof y !== 'number') return null;

  const boundaryX = x + (width ?? 0) / 2;
  const visibleW = 16;
  const visibleX = boundaryX - visibleW / 2;
  const hitW = Math.max(width ?? 0, visibleW);
  const hitX = boundaryX - hitW / 2;
  const cyMid = y + height / 2;
  const gripLen = 6;
  const gripGap = 5;

  return (
    <G>
      <Rect x={hitX} y={y} width={hitW} height={height} fill="transparent" pointerEvents="auto" />
      <Rect
        x={visibleX}
        y={y}
        width={visibleW}
        height={height}
        rx={3}
        ry={3}
        fill="#2E90FA"
        stroke="#1570EF"
        strokeWidth={1}
        pointerEvents="none"
      />
      {[-gripGap, 0, gripGap].map((dy, i) => (
        <Line
          key={i}
          x1={boundaryX - gripLen / 2}
          x2={boundaryX + gripLen / 2}
          y1={cyMid + dy}
          y2={cyMid + dy}
          stroke="white"
          strokeWidth={1.5}
          strokeLinecap="round"
          pointerEvents="none"
        />
      ))}
    </G>
  );
};

// Matches domainPadding={{ x: [0, 20] }} passed to every VictoryChart in this file.
const DOMAIN_PAD_RIGHT = 20;

type TemperatureChartProps = {
  _container?: IBoxProps;
  startDate?: Date;
  endDate?: Date;
  linkedInstances?: LinkedInstanceChartDataDto[];
  thresholds?: ThresholdsDto;
  onloadFinish?: () => void;
  // Zoom props — new for v1.6
  eventLog?: EventLog;
  zoomDomain?: { x: [number, number] } | null;
  isZoomMode?: boolean;
  // Incremented by the parent each time the user opens zoom mode — keys the
  // BrushContainer so Victory remounts fresh with no carried-over selection.
  zoomSessionId?: number;
  // The pending brush selection driven by the parent — null until the user
  // drags a range. Passed as controlled brushDomain to Victory so clearing
  // it on cancel immediately wipes the displayed selection.
  pendingZoomDomain?: { x: [number, number] } | null;
  // Fires continuously as the user drags the brush.
  onBrushDomainChange?: (domain: { x: [number, number] } | null) => void;
};

export const TemperatureChart = ({
  _container,
  startDate,
  endDate,
  linkedInstances,
  thresholds,
  eventLog,
  zoomDomain,
  isZoomMode,
  zoomSessionId,
  pendingZoomDomain,
  onBrushDomainChange,
  ...props
}: TemperatureChartProps) => {
  const hasPendingBrush = !!pendingZoomDomain;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isTabletScreen] = useMediaQuery({ maxWidth: 1200 });
  const [layout, setLayout] = useState<LayoutRectangle>();
  const [tickValues, setTickValues] = useState<number[]>();

  // The "active" range drives X-axis ticks and the chart's visible domain.
  // When zoomed in, zoomDomain takes precedence.
  const activeStart = zoomDomain ? new Date(zoomDomain.x[0]) : startDate;
  const activeEnd = zoomDomain ? new Date(zoomDomain.x[1]) : endDate;

  const totalHours = moment(activeEnd).diff(activeStart, 'hours');
  const days = Math.round(moment(activeEnd).diff(moment(activeStart), 'hours') / 24);

  const chart: {
    datasets: { x: number; y?: number | null }[];
    max: number;
    min: number;
    ticks: number[];
    thresholdTicks: number[];
    isDoorEnabled?: boolean;
    gradient: { offset: number; color: string }[];
    signalDatasets: ReturnType<typeof createMetricsets>;
    doorDatasets: ReturnType<typeof createMetricsets>;
  } = useMemo(() => {
    const linkedTemperature = find(linkedInstances, _ => !!_.Temperature);
    const linkedDoor = find(linkedInstances, _ => !!_.Door);

    const statistics = linkedTemperature?.Temperature?.Statistics;

    let datasets: { x: number; y?: number | null }[] = [];
    linkedTemperature?.Temperature?.Data?.forEach((_, i) => {
      const timestamp = new Date(_.Timestamp as string)!;
      const xValue = timestamp.getTime();
      const yValue: number | null = _.Value!;

      if (i >= 1) {
        const previousTimestamp = new Date(linkedTemperature?.Temperature?.Data![i - 1].Timestamp as string);
        const diffInMs = Math.abs(timestamp.getTime() - previousTimestamp.getTime());
        const diffInMinutes = diffInMs / (5 * 60 * 1000);
        if (diffInMinutes > 1) {
          datasets.push({ x: xValue - 5 * 60 * 1000, y: null });
        }
      }
      datasets.push({ x: xValue, y: yValue });
    });

    // Y-axis domain always expands to include the configured red thresholds so
    // the operator can see them even when current readings are well inside the
    // safe range.
    const dataMax = Math.round(statistics?.Max ?? 0);
    const dataMin = Math.round(statistics?.Min ?? 0);
    const upperRedTick = typeof thresholds?.UpperRed === 'number' ? Math.round(thresholds.UpperRed) : undefined;
    const lowerRedTick = typeof thresholds?.LowerRed === 'number' ? Math.round(thresholds.LowerRed) : undefined;
    const maxValue = Math.max(dataMax, upperRedTick ?? dataMax) + 1;
    const minValue = Math.min(dataMin, lowerRedTick ?? dataMin) - 1;
    const numTicks = 6;

    let ticks: number[] = [];
    const interval = (maxValue - minValue) / (numTicks - 1);
    for (let i = 0; i < numTicks; i++) {
      ticks.push(Math.round(minValue + i * interval));
    }

    // Only Red thresholds get a horizontal reference line.
    const thresholdTicks = [thresholds?.UpperRed, thresholds?.LowerRed].filter(
      (tick): tick is number => typeof tick === 'number' && tick > minValue && tick <= maxValue,
    );

    // Gradient offsets computed against the chart's full Y domain (maxValue /
    // minValue) with gradientUnits="userSpaceOnUse" so every segment of the
    // line picks up the correct zone color.
    const upperTemp = maxValue;
    const lowerTemp = minValue;

    const upperRed = getThresholdOffset(thresholds?.UpperRed ?? 0, lowerTemp, upperTemp);
    const upperYellow = getThresholdOffset(thresholds?.UpperYellow ?? 0, lowerTemp, upperTemp);
    const upperGreen = getThresholdOffset(thresholds?.UpperGreen ?? 0, lowerTemp, upperTemp);
    const targetUpperBound = getThresholdOffset(thresholds?.TargetUpperBound ?? 0, lowerTemp, upperTemp);
    const targetLowerBound = getThresholdOffset(thresholds?.TargetLowerBound ?? 0, lowerTemp, upperTemp);
    const lowerGreen = getThresholdOffset(thresholds?.LowerGreen ?? 0, lowerTemp, upperTemp);
    const lowerYellow = getThresholdOffset(thresholds?.LowerYellow ?? 0, lowerTemp, upperTemp);
    const lowerRed = getThresholdOffset(thresholds?.LowerRed ?? 0, lowerTemp, upperTemp);

    const gradient = [
      { offset: 0, color: ThresholdColors.Red },
      { offset: upperRed, color: ThresholdColors.Red },
      { offset: upperRed, color: ThresholdColors.Yellow },
      { offset: upperYellow, color: ThresholdColors.Yellow },
      { offset: upperYellow, color: ThresholdColors.Green },
      { offset: upperGreen, color: ThresholdColors.Green },
      { offset: upperGreen, color: ThresholdColors.Blue },
      { offset: targetUpperBound, color: ThresholdColors.Blue },
      { offset: targetLowerBound, color: ThresholdColors.Blue },
      { offset: lowerGreen, color: ThresholdColors.Blue },
      { offset: lowerGreen, color: ThresholdColors.Green },
      { offset: lowerYellow, color: ThresholdColors.Green },
      { offset: lowerYellow, color: ThresholdColors.Yellow },
      { offset: lowerRed, color: ThresholdColors.Yellow },
      { offset: lowerRed, color: ThresholdColors.Red },
      { offset: 1, color: ThresholdColors.Red },
    ];

    const signalDatasets = createMetricsets(linkedTemperature?.RealTimeStatus?.Data);
    const doorDatasets = createMetricsets(linkedDoor?.Door?.Data, FIVE_MIN_MS);

    return {
      datasets: datasets ?? [],
      max: maxValue,
      min: minValue,
      ticks,
      thresholdTicks: thresholdTicks ?? [],
      gradient,
      isDoorEnabled: !!linkedDoor,
      signalDatasets,
      doorDatasets,
    };
  }, [linkedInstances, thresholds]);

  const handleChangeLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    setLayout(nativeEvent.layout);
  };

  // Fallback dimensions when onLayout hasn't fired yet.
  const chartW = layout?.width ?? 1200;
  const chartH = isTabletScreen ? 260 : 320;

  // Chart padding — mirrors the VictoryChart padding prop so coordinate mapping is consistent.
  const padLeft = isTabletScreen ? 40 : 50;
  const padRight = isTabletScreen ? 52 : 60;

  const {
    handleTouchMove,
    handleTouchEnd,
    tooltip: nativeTooltip,
  } = useChartGesture({
    enabled: !isZoomMode && !isEmpty(chart.datasets),
    chartW,
    chartH,
    padding: { top: 30, left: padLeft, bottom: 40, right: padRight },
    domainPadRight: DOMAIN_PAD_RIGHT,
    domain: {
      x: [activeStart?.getTime() ?? 0, activeEnd?.getTime() ?? 0],
      y: [chart.min, chart.max],
    },
    datasets: chart.datasets,
  });

  // Native brush selection state — tracks the raw pixel x-coordinates of the drag.
  // Only used on native (web uses VictoryBrushContainer whose PanResponder works
  // correctly in the browser but is unreliable on native due to pointerEvents="box-none").
  const [nativeBrushPixels, setNativeBrushPixels] = useState<{ startX: number; endX: number } | null>(null);

  // Clear brush pixels whenever zoom mode exits so stale pixels don't leak
  // into the next zoom session.
  useEffect(() => {
    if (!isZoomMode) setNativeBrushPixels(null);
  }, [isZoomMode]);

  // Tracks the x-pixel where the brush drag started. Stored in a ref so it's
  // readable in handleBrushMove without triggering re-renders or stale closures.
  const brushStartXRef = useRef<number | null>(null);

  // Map a pixel x-coordinate (relative to the overlay View) to a domain timestamp.
  const pixelToDomain = (pixelX: number): number => {
    const plotW = chartW - padLeft - padRight - DOMAIN_PAD_RIGHT;
    const xMin = activeStart?.getTime() ?? 0;
    const xMax = activeEnd?.getTime() ?? 0;
    const clamped = Math.max(padLeft, Math.min(padLeft + plotW, pixelX));
    return xMin + ((clamped - padLeft) / plotW) * (xMax - xMin);
  };

  const handleBrushGrant = (locationX: number) => {
    brushStartXRef.current = locationX;
    setNativeBrushPixels({ startX: locationX, endX: locationX });
  };

  const handleBrushMove = (locationX: number) => {
    const startX = brushStartXRef.current;
    if (startX === null) return;

    // Plain setState call (no updater) so React never runs it during the render phase.
    setNativeBrushPixels({ startX, endX: locationX });

    const d0 = pixelToDomain(startX);
    const d1 = pixelToDomain(locationX);
    // Require at least 1-minute span to avoid treating taps as selections.
    if (Math.abs(d0 - d1) < 60 * 1000) {
      onBrushDomainChange?.(null);
    } else {
      onBrushDomainChange?.({ x: [Math.min(d0, d1), Math.max(d0, d1)] });
    }
  };

  useEffect(() => {
    if (activeStart && activeEnd) {
      let ticks: number[] = [];
      if (totalHours < 6) {
        let startTime = activeStart;
        ticks.push(startTime.getTime());
        while (startTime < activeEnd) {
          startTime = new Date(startTime.getTime() + 30 * 60 * 1000);
          ticks.push(startTime.getTime());
        }
        setTickValues(ticks);
        return;
      }

      if (days <= 1) {
        ticks = range(5).map(num => activeStart.getTime() + num * 6 * 60 * 60 * 1000);
      } else if (days >= 90) {
        ticks = [
          activeStart.getTime(),
          ...range(1, 10).map(i =>
            moment(activeStart)
              .add(i * 10, 'days')
              .valueOf(),
          ),
        ];
      } else if (days >= 30) {
        ticks = [
          activeStart.getTime(),
          moment(activeStart).add(7, 'days').valueOf(),
          moment(activeStart).add(15, 'days').valueOf(),
          moment(activeStart).add(22, 'days').valueOf(),
          moment(activeStart).add(30, 'days').valueOf(),
        ];
      } else {
        range(days + 1).forEach(d => ticks.push(moment(activeStart).add(d, 'days').valueOf()));
      }
      setTickValues(ticks);
    }
  }, [activeStart?.getTime(), activeEnd?.getTime()]);

  const chartIsEmpty = isEmpty(chart.datasets);

  useEffect(() => {
    if (layout?.height && !chartIsEmpty && props?.onloadFinish) {
      const timer = setTimeout(() => {
        props.onloadFinish?.();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [layout?.height, chartIsEmpty, props?.onloadFinish, linkedInstances, chart.datasets]);

  return (
    <View w="full">
      <Box
        w="full"
        h="full"
        height={{ base: chart.isDoorEnabled ? 305 : 280, xl: chart.isDoorEnabled ? 365 : 340 }}
        onLayout={handleChangeLayout}
        // Dragging the brush was also selecting surrounding text — disable
        // user-select on the chart container to keep drag behavior clean.
        style={{ userSelect: 'none', WebkitUserSelect: 'none' } as any}
        {..._container}
      >
        {isUndefined(linkedInstances) && (
          <Box h="full" w="full" alignItems="center" justifyContent="center">
            <Text size="lg" color="gray.600" fontWeight={400}>
              {t('store_equipment:no_chart_data')}
            </Text>
          </Box>
        )}

        {/* Temperature Metrics */}
        {!isEmpty(chart.datasets) && (
          <VictoryChart
            key={zoomSessionId ?? 0}
            width={chartW}
            height={chartH}
            containerComponent={
              // On native, all interaction (tooltip + brush) is handled by our own
              // View responder overlays. Plain VictoryContainer on native in all modes.
              Platform.OS !== 'web' ? (
                <VictoryContainer />
              ) : isZoomMode ? (
                // Web zoom: VictoryBrushContainer handles mouse drag selection.
                <VictoryBrushContainer
                  key={`brush-${zoomSessionId ?? 0}`}
                  brushDomain={pendingZoomDomain ?? undefined}
                  brushDimension="x"
                  brushStyle={{
                    stroke: hasPendingBrush ? colors.blue[400] : 'none',
                    strokeWidth: hasPendingBrush ? 1 : 0,
                    fill: hasPendingBrush ? colors.blue[100] : 'none',
                    fillOpacity: hasPendingBrush ? 0.35 : 0,
                  }}
                  handleComponent={<BrushHandle hasSelection={hasPendingBrush} />}
                  handleWidth={40}
                  defaultBrushArea="none"
                  onBrushDomainChange={(domain: any) => {
                    if (!domain?.x) {
                      onBrushDomainChange?.(null);
                      return;
                    }
                    const [x0, x1] = domain.x;
                    const start = typeof x0 === 'number' ? x0 : new Date(x0).getTime();
                    const end = typeof x1 === 'number' ? x1 : new Date(x1).getTime();
                    if (Math.abs(end - start) < 60 * 1000) {
                      onBrushDomainChange?.(null);
                      return;
                    }
                    onBrushDomainChange?.({ x: [Math.min(start, end), Math.max(start, end)] });
                  }}
                />
              ) : (
                // Web normal: mouse overlay + SVG flyout handles the tooltip
                // (VictoryVoronoiContainer's onMouseMove is broken in react-native-svg v13).
                <VictoryContainer />
              )
            }
            padding={{ top: 30, left: isTabletScreen ? 40 : 50, bottom: 40, right: isTabletScreen ? 52 : 60 }}
            domainPadding={{ x: [0, 20] }}
            domain={{ x: [activeStart?.getTime() ?? 0, activeEnd?.getTime() ?? 0], y: [chart.min, chart.max] }}
            theme={{
              chart: {},
              axis: {
                style: {
                  axisLabel: {
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    fontSize: 12,
                  },
                  tickLabels: {
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    fontSize: 12,
                  },
                },
              },
            }}
          >
            {/* Event log highlight — gray shaded area over the selected event's time span */}
            {typeof chart.max === 'number' && !!eventLog?.startTimeMs && (
              <VictoryArea
                data={[
                  { x: eventLog.startTimeMs, y: chart.max },
                  { x: eventLog.endTimeMs ?? endDate?.getTime(), y: chart.max },
                ]}
                x="x"
                y="y"
                y0={chart.min}
                style={{ data: { fill: colors.gray[200] } }}
              />
            )}

            {/* Vertical gradient anchored to absolute Y pixel coordinates so
                every segment of the data line picks up the correct zone color
                regardless of null-gap splits. */}
            <LinearGradient id="thresholdColors" gradientUnits="userSpaceOnUse" x1={0} x2={0} y1={30} y2={chartH - 40}>
              {chart.gradient.map((_gradient, i) => (
                <Stop key={`linear_${i}`} offset={_gradient.offset} stopColor={_gradient.color} />
              ))}
            </LinearGradient>

            {/* X-axis */}
            <VictoryAxis
              style={{
                axis: { stroke: colors.gray[200], strokeWidth: 0.5 },
                grid: { stroke: 'transparent' },
                tickLabels: {
                  padding: 15,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: 11,
                  fill: colors.gray[500],
                },
              }}
              offsetY={40}
              orientation="bottom"
              scale={{ x: 'time' }}
              tickFormat={(tick: number, i: number) => {
                if (totalHours < 6) return moment(tick).format(i === 0 ? 'MMM DD' : 'h:mm A');
                return moment(tick).format(`${days <= 1 ? `${i === 0 || i === 4 ? 'MMM DD' : 'hh A'}` : 'MMM DD'}`);
              }}
              tickValues={tickValues}
            />

            {/* Y-axis — quiet tick labels */}
            <VictoryAxis
              dependentAxis
              tickValues={chart.ticks}
              style={{
                axis: { stroke: 'transparent', strokeWidth: 0 },
                grid: { stroke: 'transparent', strokeWidth: 0 },
                ticks: { stroke: 'transparent', size: 1 },
                tickLabels: {
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  fontSize: 11,
                  fill: colors.gray[500],
                  padding: 10,
                },
              }}
            />

            <VictoryLine
              interpolation="monotoneX"
              data={chart.datasets}
              style={{
                data: {
                  stroke: 'url(#thresholdColors)',
                  strokeWidth: days > 7 ? 1.25 : days > 1 ? 1.5 : 2,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                },
                parent: { border: '1px solid #cc0000' },
              }}
            />

            {/* Red threshold reference lines — rendered after data line so they
                sit on top as clean reference markers. */}
            <VictoryAxis
              dependentAxis
              tickValues={chart.thresholdTicks}
              orientation="right"
              style={{
                axis: { stroke: 'transparent', strokeWidth: 0 },
                ticks: { stroke: 'transparent', size: 0 },
                grid: {
                  stroke: colors.error[300],
                  strokeWidth: 2,
                  strokeOpacity: 1,
                  strokeLinecap: 'round',
                  strokeDasharray: '0 6',
                },
              }}
              tickLabelComponent={<ThresholdChip />}
              tickFormat={(tick: number) => `${tick}°`}
            />
          </VictoryChart>
        )}

        {/* Native tooltip: transparent touch-capture area over the chart.
            Uses RN's built-in View responder system (locationX is already relative
            to this View, so no extra coordinate math is needed).
            Hidden during zoom mode so VictoryBrushContainer receives the touch. */}
        {Platform.OS !== 'web' && !isZoomMode && !isEmpty(chart.datasets) && (
          <View
            position="absolute"
            top={0}
            left={0}
            right={0}
            height={chartH}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={e => handleTouchMove(e.nativeEvent.locationX)}
            onResponderMove={e => handleTouchMove(e.nativeEvent.locationX)}
            onResponderRelease={handleTouchEnd}
            onResponderTerminate={handleTouchEnd}
          />
        )}

        {/* Native zoom: transparent drag-capture area. Uses the same View responder
            approach as the tooltip so no GestureHandlerRootView is required. */}
        {Platform.OS !== 'web' && isZoomMode && !isEmpty(chart.datasets) && (
          <View
            position="absolute"
            top={0}
            left={0}
            right={0}
            height={chartH}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={e => handleBrushGrant(e.nativeEvent.locationX)}
            onResponderMove={e => handleBrushMove(e.nativeEvent.locationX)}
            onResponderRelease={() => {}}
            onResponderTerminate={() => {}}
          />
        )}

        {/* Native zoom: SVG overlay showing the blue selection rectangle. */}
        {Platform.OS !== 'web' && isZoomMode && !!nativeBrushPixels && (
          <Svg style={{ position: 'absolute', top: 0, left: 0 }} width={chartW} height={chartH} pointerEvents="none">
            <Rect
              x={Math.min(nativeBrushPixels.startX, nativeBrushPixels.endX)}
              y={30}
              width={Math.abs(nativeBrushPixels.startX - nativeBrushPixels.endX)}
              height={chartH - 70}
              fill={colors.blue[100]}
              fillOpacity={0.35}
              stroke={colors.blue[400]}
              strokeWidth={1}
            />
          </Svg>
        )}

        {/* Native tooltip: SVG overlay rendered on top of the chart at the same
            pixel dimensions so coordinates map 1:1 with the chart's SVG space. */}
        {Platform.OS !== 'web' && !isZoomMode && !!nativeTooltip && (
          <Svg style={{ position: 'absolute', top: 0, left: 0 }} width={chartW} height={chartH} pointerEvents="none">
            <ChartTooltipFlyout
              active
              text="show"
              datum={nativeTooltip.datum}
              x={nativeTooltip.crosshairX}
              y={nativeTooltip.crosshairY}
              signalDatasets={chart.signalDatasets as any}
              doorDatasets={chart.doorDatasets as any}
              hasDoor={chart.isDoorEnabled}
              thresholds={thresholds as any}
              chartWidth={chartW}
              axisY={chartH - 40}
            />
          </Svg>
        )}

        {/* Web tooltip: transparent mouse-capture overlay.
            P1 (react-frontends) uses VictoryVoronoiContainer for hover, but that
            requires react-native-svg v15 + victory-native v37. This project is on
            older versions where hover does not work with VictoryVoronoiContainer.
            Workaround: use a plain VictoryContainer and track mouse position
            manually via a View overlay, same as the native touch tooltip above. */}
        {Platform.OS === 'web' && !isZoomMode && !isEmpty(chart.datasets) && (
          <View
            position="absolute"
            top={0}
            left={0}
            right={0}
            height={chartH}
            // @ts-expect-error web-only mouse events not in RN View types
            onMouseMove={(e: any) => {
              const rect = e.currentTarget.getBoundingClientRect();
              handleTouchMove(e.clientX - rect.left);
            }}
            onMouseLeave={handleTouchEnd}
            style={{ cursor: 'crosshair' } as any}
          />
        )}
        {Platform.OS === 'web' && !isZoomMode && !!nativeTooltip && (
          <Svg style={{ position: 'absolute', top: 0, left: 0 }} width={chartW} height={chartH} pointerEvents="none">
            <ChartTooltipFlyout
              active
              text="show"
              datum={nativeTooltip.datum}
              x={nativeTooltip.crosshairX}
              y={nativeTooltip.crosshairY}
              signalDatasets={chart.signalDatasets as any}
              doorDatasets={chart.doorDatasets as any}
              hasDoor={chart.isDoorEnabled}
              thresholds={thresholds as any}
              chartWidth={chartW}
              axisY={chartH - 40}
            />
          </Svg>
        )}

        {/* Status bars (signal + door) — use the exact same width/padding/domainPadding
            as the main VictoryChart so both share the same SVG coordinate system.
            That guarantees X-axis alignment without any CSS positioning math. */}
        {(() => {
          const dataPadRight = DOMAIN_PAD_RIGHT;
          const barWidth = chartW - padLeft - padRight - dataPadRight;

          const renderBar = (key: string, Icon: any, datasets: typeof chart.signalDatasets, colorFor: (v: number) => string, fillRange = true) => (
            <Box position="relative" width={chartW} mt={2} height={4}>
              {/* Icon sits left of the Y-axis */}
              <Box position="absolute" left={2} top={0} bottom={0} justifyContent="center" zIndex={1}>
                <Icon size={3.5} color={colors.gray[600]} />
              </Box>
              {/* Border overlay — spans the same pixel range as the data area */}
              <Box
                position="absolute"
                left={padLeft}
                top={0}
                width={barWidth}
                height={4}
                borderWidth={{ base: 0, xl: 1 }}
                borderColor="gray.200"
                pointerEvents="none"
                zIndex={2}
              />
              {/* Bar chart — identical width / padding / domainPadding to the main
                  chart, so Victory maps data timestamps to the same screen pixels. */}
              <VictoryChart
                width={chartW}
                height={16}
                scale={{ x: 'time' }}
                domain={{ x: [activeStart?.getTime() ?? 0, activeEnd?.getTime() ?? 0], y: [-1, 1] }}
                domainPadding={{ x: [0, 20] }}
                padding={{ top: 0, left: padLeft, bottom: 0, right: padRight }}
                style={{ background: { fill: 'transparent' } }}
              >
                <VictoryAxis
                  tickValues={[activeStart?.getTime(), activeEnd?.getTime()]}
                  style={{
                    axis: { strokeWidth: 0 },
                    grid: { strokeWidth: 0 },
                    tickLabels: { fontSize: 0, fill: 'transparent' },
                  }}
                />
                {datasets.map((data, index) => {
                  const isFirst = index === 0;
                  const isLast = index === datasets.length - 1;
                  const startX = fillRange && isFirst ? (activeStart?.getTime() ?? data.startTime) : data.startTime;
                  const endX = fillRange && isLast ? (activeEnd?.getTime() ?? data.endTime) : data.endTime;
                  return (
                    <VictoryLine
                      key={`${key}-${index}`}
                      style={{ data: { stroke: colorFor(data.value), strokeWidth: 32 } }}
                      data={[
                        { x: startX, y: 0 },
                        { x: endX, y: 0 },
                      ]}
                    />
                  );
                })}
              </VictoryChart>
            </Box>
          );

          const SIGNAL_ON = colors.teal[500];
          const SIGNAL_OFF = colors.teal[200];
          const DOOR_CLOSED = colors.fuchsia[300];
          const DOOR_OPEN = colors.fuchsia[500];

          return (
            <>
              {renderBar('signal', WifiIcon, chart.signalDatasets, (v: number) => (v === 1 ? SIGNAL_ON : SIGNAL_OFF))}
              {chart.isDoorEnabled &&
                renderBar('door', DoorEventIcon, chart.doorDatasets, (v: number) => (v === 1 ? DOOR_CLOSED : DOOR_OPEN), false)}
            </>
          );
        })()}
      </Box>
    </View>
  );
};
